import { useEffect, useState } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Grid,
  Skeleton,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Divider,
  Collapse
} from '@mui/material';
import Icon from '@mui/material/Icon';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { Can } from 'context';
import { getClient, getProviderConfig } from 'services/clients';

function ProviderConfig() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [copiedField, setCopiedField] = useState(null);
  const [expandedEndpoints, setExpandedEndpoints] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientRes = await getClient(id);
        setClient(clientRes);
      } catch (err) {
        console.log('Failed to load client:', err);
      }

      try {
        const configRes = await getProviderConfig(id);
        setConfig(configRes);
        // Auto-expand all endpoints
        if (configRes?.endpoints && typeof configRes.endpoints === 'object') {
          const expanded = {};
          Object.keys(configRes.endpoints).forEach((key) => {
            expanded[key] = true;
          });
          setExpandedEndpoints(expanded);
        }
      } catch (err) {
        console.log('Failed to load provider config:', err);
        const status = err?.response?.status;
        if (status === 404) {
          setError('Provider configuration not found for this integration.');
        } else if (status === 403) {
          setError('You do not have permission to view this configuration.');
        } else {
          setError('Failed to load provider configuration. Please try again.');
        }
      }

      setLoading(false);
    };

    fetchData();
  }, [id]);

  const handleCopy = (text, fieldName) => {
    try {
      const content = typeof text === 'object' ? JSON.stringify(text, null, 2) : String(text);
      navigator.clipboard
        .writeText(content)
        .then(() => {
          setCopiedField(fieldName);
          setTimeout(() => setCopiedField(null), 2000);
        })
        .catch(() => {
          // Fallback for non-HTTPS or older browsers
          const textarea = document.createElement('textarea');
          textarea.value = content;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
          setCopiedField(fieldName);
          setTimeout(() => setCopiedField(null), 2000);
        });
    } catch (e) {
      console.log('Copy failed:', e);
    }
  };

  const toggleEndpoint = (key) => {
    setExpandedEndpoints((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderTemplateBlock = (label, template, fieldName) => {
    if (!template) return null;

    // Coerce to string if template is unexpectedly an object (malformed JSONB)
    const templateStr = typeof template === 'object' ? JSON.stringify(template, null, 2) : String(template);

    let formatted = templateStr;
    try {
      const parsed = JSON.parse(templateStr);
      formatted = JSON.stringify(parsed, null, 2);
    } catch (e) {
      // not JSON, keep as-is
    }

    return (
      <MDBox mb={2}>
        <MDBox display='flex' alignItems='center' justifyContent='space-between' mb={0.5}>
          <MDTypography variant='caption' fontWeight='bold' color='text' textTransform='uppercase'>
            {label}
          </MDTypography>
          <Tooltip title={copiedField === fieldName ? 'Copied!' : 'Copy'}>
            <IconButton size='small' onClick={() => handleCopy(templateStr, fieldName)}>
              <Icon fontSize='small' color={copiedField === fieldName ? 'success' : 'inherit'}>
                {copiedField === fieldName ? 'check' : 'content_copy'}
              </Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
        <MDBox
          sx={{
            backgroundColor: ({ palette }) => palette.grey[100],
            borderRadius: '8px',
            p: 1.5,
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            lineHeight: 1.6,
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            border: '1px solid',
            borderColor: ({ palette }) => palette.grey[300]
          }}
        >
          {formatted}
        </MDBox>
      </MDBox>
    );
  };

  const renderPreRequestSteps = (steps, endpointKey) => {
    if (!Array.isArray(steps) || steps.length === 0) return null;

    return (
      <MDBox mb={2}>
        <MDTypography variant='caption' fontWeight='bold' color='text' textTransform='uppercase'>
          Pre-Request Steps
        </MDTypography>
        {steps
          .filter((s) => s && typeof s === 'object')
          .map((step, idx) => (
            <MDBox
              key={idx}
              mt={1}
              sx={{
                backgroundColor: ({ palette }) => palette.grey[50],
                borderRadius: '8px',
                p: 1.5,
                border: '1px solid',
                borderColor: ({ palette }) => palette.grey[200]
              }}
            >
              <MDBox display='flex' alignItems='center' gap={1} mb={1}>
                <Chip
                  label={step.type || 'unknown'}
                  size='small'
                  color='info'
                  variant='outlined'
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                />
                {step.algorithm && (
                  <Chip
                    label={step.algorithm}
                    size='small'
                    color='warning'
                    variant='outlined'
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                  />
                )}
                {step.headerName && (
                  <Chip
                    label={step.headerName}
                    size='small'
                    color='secondary'
                    variant='outlined'
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                  />
                )}
              </MDBox>
              {step.payloadTemplate &&
                renderTemplateBlock('Payload Template', step.payloadTemplate, `${endpointKey}-step-${idx}-payload`)}
              {step.signatureTemplate &&
                renderTemplateBlock(
                  'Signature Template',
                  step.signatureTemplate,
                  `${endpointKey}-step-${idx}-signature`
                )}
              {step.secretTemplate &&
                renderTemplateBlock('Secret Template', '••••••••', `${endpointKey}-step-${idx}-secret`)}
            </MDBox>
          ))}
      </MDBox>
    );
  };

  const renderEndpointCard = (key, endpoint) => {
    if (!endpoint || typeof endpoint !== 'object') return null;
    const isExpanded = expandedEndpoints[key] !== false;
    const method = endpoint.method || 'POST';
    const methodColors = {
      GET: 'success',
      POST: 'info',
      PUT: 'warning',
      DELETE: 'error',
      PATCH: 'secondary'
    };

    return (
      <MDBox mb={2}>
        <MDBox
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          onClick={() => toggleEndpoint(key)}
          sx={{
            cursor: 'pointer',
            backgroundColor: ({ palette }) => palette.grey[50],
            borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
            p: 1.5,
            border: '1px solid',
            borderColor: ({ palette }) => palette.grey[300],
            '&:hover': {
              backgroundColor: ({ palette }) => palette.grey[100]
            }
          }}
        >
          <MDBox display='flex' alignItems='center' gap={1}>
            <Chip
              label={method}
              size='small'
              color={methodColors[method] || 'default'}
              sx={{ fontWeight: 'bold', minWidth: 60 }}
            />
            <MDTypography variant='button' fontWeight='bold' textTransform='capitalize'>
              {key}
            </MDTypography>
            {endpoint.url && (
              <MDTypography variant='caption' color='text' sx={{ fontFamily: 'monospace' }}>
                {endpoint.url}
              </MDTypography>
            )}
          </MDBox>
          <Icon fontSize='small'>{isExpanded ? 'expand_less' : 'expand_more'}</Icon>
        </MDBox>
        <Collapse in={isExpanded}>
          <MDBox
            sx={{
              borderRadius: '0 0 8px 8px',
              p: 2,
              border: '1px solid',
              borderTop: 'none',
              borderColor: ({ palette }) => palette.grey[300]
            }}
          >
            {endpoint.bodyTemplate && renderTemplateBlock('Body Template', endpoint.bodyTemplate, `${key}-body`)}
            {endpoint.queryTemplate && renderTemplateBlock('Query Template', endpoint.queryTemplate, `${key}-query`)}
            {endpoint.responseMapping &&
              renderTemplateBlock('Response Mapping', endpoint.responseMapping, `${key}-response`)}
            {endpoint.preRequestSteps && renderPreRequestSteps(endpoint.preRequestSteps, key)}
            {!endpoint.bodyTemplate &&
              !endpoint.queryTemplate &&
              !endpoint.responseMapping &&
              !endpoint.preRequestSteps && (
                <MDTypography variant='body2' color='text'>
                  No templates configured for this endpoint.
                </MDTypography>
              )}
          </MDBox>
        </Collapse>
      </MDBox>
    );
  };

  const renderEndpointsTab = () => {
    if (!config?.endpoints || Object.keys(config.endpoints).length === 0) {
      return (
        <MDBox p={3} textAlign='center'>
          <Icon fontSize='large' color='disabled'>
            settings_suggest
          </Icon>
          <MDTypography variant='body2' color='text' mt={1}>
            No endpoints configured
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <MDBox p={2}>
        {Object.entries(config.endpoints).map(([key, endpoint]) => (
          <div key={key}>{renderEndpointCard(key, endpoint)}</div>
        ))}
      </MDBox>
    );
  };

  const renderOverviewTab = () => {
    const fields = [];
    if (config?.baseUrl) fields.push({ label: 'Base URL', value: config.baseUrl });
    if (config?.authType) fields.push({ label: 'Auth Type', value: config.authType });
    if (config?.providerName) fields.push({ label: 'Provider Name', value: config.providerName });

    return (
      <MDBox p={2}>
        {fields.length > 0 ? (
          <Grid container spacing={2}>
            {fields.map((field) => (
              <Grid item xs={12} md={6} key={field.label}>
                <MDBox
                  sx={{
                    backgroundColor: ({ palette }) => palette.grey[50],
                    borderRadius: '8px',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: ({ palette }) => palette.grey[200]
                  }}
                >
                  <MDTypography variant='caption' fontWeight='bold' color='text' textTransform='uppercase'>
                    {field.label}
                  </MDTypography>
                  <MDTypography variant='body2' sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                    {field.value}
                  </MDTypography>
                </MDBox>
              </Grid>
            ))}
          </Grid>
        ) : null}

        <MDBox mt={3}>
          <MDBox display='flex' alignItems='center' justifyContent='space-between' mb={1}>
            <MDTypography variant='button' fontWeight='bold'>
              Raw Configuration
            </MDTypography>
            <Tooltip title={copiedField === 'raw' ? 'Copied!' : 'Copy JSON'}>
              <IconButton size='small' onClick={() => handleCopy(config, 'raw')}>
                <Icon fontSize='small' color={copiedField === 'raw' ? 'success' : 'inherit'}>
                  {copiedField === 'raw' ? 'check' : 'content_copy'}
                </Icon>
              </IconButton>
            </Tooltip>
          </MDBox>
          <MDBox
            sx={{
              backgroundColor: ({ palette }) => palette.grey[100],
              borderRadius: '8px',
              p: 2,
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              lineHeight: 1.6,
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              maxHeight: 500,
              overflow: 'auto',
              border: '1px solid',
              borderColor: ({ palette }) => palette.grey[300]
            }}
          >
            {(() => {
              try {
                return JSON.stringify(config, null, 2);
              } catch (e) {
                return 'Unable to serialize configuration';
              }
            })()}
          </MDBox>
        </MDBox>
      </MDBox>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <Skeleton variant='rectangular' height={400} sx={{ borderRadius: '12px' }} />;
    }

    const noConfig = !config || (typeof config === 'object' && !config.endpoints);
    if (error || noConfig) {
      const isError = !!error;
      return (
        <Card sx={{ borderRadius: '12px' }}>
          <MDBox p={4} textAlign='center'>
            <Icon fontSize='large' color={isError ? 'error' : 'disabled'}>
              {isError ? 'error_outline' : 'settings_suggest'}
            </Icon>
            <MDTypography variant='h6' mt={1}>
              {error || 'No provider configuration found for this integration.'}
            </MDTypography>
            {!isError && (
              <MDTypography variant='body2' color='text' mt={0.5}>
                This integration does not have a provider configuration set up yet.
              </MDTypography>
            )}
            <MDButton variant='outlined' color='info' onClick={() => navigate('/client-management')} sx={{ mt: 2 }}>
              Back to Integrations
            </MDButton>
          </MDBox>
        </Card>
      );
    }

    return (
      <Card sx={{ borderRadius: '12px', overflow: 'visible' }}>
        <MDBox p={3} pb={1}>
          <MDBox display='flex' alignItems='center' justifyContent='space-between'>
            <MDBox>
              <MDTypography variant='h5' fontWeight='bold'>
                Provider Configuration
              </MDTypography>
              <MDTypography variant='body2' color='text'>
                {client?.name || id}
              </MDTypography>
            </MDBox>
            <MDButton variant='outlined' color='dark' size='small' onClick={() => navigate('/client-management')}>
              <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
              Back
            </MDButton>
          </MDBox>
          <Divider sx={{ my: 1.5 }} />
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 1 }}>
            <Tab
              label='Endpoints'
              icon={<Icon fontSize='small'>api</Icon>}
              iconPosition='start'
              sx={{ minHeight: 48 }}
            />
            <Tab
              label='Overview & Raw JSON'
              icon={<Icon fontSize='small'>code</Icon>}
              iconPosition='start'
              sx={{ minHeight: 48 }}
            />
          </Tabs>
        </MDBox>
        {activeTab === 0 && renderEndpointsTab()}
        {activeTab === 1 && renderOverviewTab()}
      </Card>
    );
  };

  return (
    <>
      <Can I='read' a='client'>
        <DashboardLayout>
          <DashboardNavbar />
          <MDBox py={3}>
            <Grid container justifyContent='center'>
              <Grid item xs={12} lg={10} xl={8}>
                {renderContent()}
              </Grid>
            </Grid>
          </MDBox>
          <Snackbar
            open={!!copiedField}
            autoHideDuration={2000}
            onClose={() => setCopiedField(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity='success' sx={{ width: '100%' }}>
              Copied to clipboard!
            </Alert>
          </Snackbar>
        </DashboardLayout>
      </Can>
      <Can not I='read' a='client'>
        <Navigate to='/dashboard' replace />
      </Can>
    </>
  );
}

export default ProviderConfig;
